import { useState, useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import {
  Area,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Line,
  ReferenceLine,
} from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import {
  Activity,
  BarChart2,
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  HelpCircle,
  Map as MapIcon,
  Monitor,
  MousePointerClick,
  Repeat2,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useDashReport } from '@/modules/dash/hooks/useDashReport'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { MapaDepartamentosPeru } from '../components/MapaDepartamentosPeru'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

// ── Tipos ──────────────────────────────────────────────────────────────────

interface Performance {
  impressions: number
  clicks: number
  ctr: number
  viewability: number
  unique_impressions: number
  frequency: number
}

interface CiudadItem   { region: string;    impressions: number; clicks: number; ctr: number; mes: string }
interface CreativoItem { creativo: string;  impressions: number; clicks: number; ctr: number; mes: string }
interface DispositivoItem { dispositivo: string; impressions: number; clicks: number; ctr: number; mes: string }
interface DiarioItem   { fecha: string;     impressions: number; clicks: number; ctr: number; mes: string }

interface MesData {
  totales:     { impressions: number; clicks: number; ctr: number }
  performance: Performance
  ciudad:      CiudadItem[]
  creativo:    CreativoItem[]
  dispositivo: DispositivoItem[]
  diario:      DiarioItem[]
}

interface ProgrammaticData {
  anual: { impressions: number; clicks: number; ctr: number }
  meses: Record<string, MesData>
}

// ── Constantes ─────────────────────────────────────────────────────────────

const MESES = ['octubre', 'noviembre', 'diciembre'] as const
const MES_LABELS: Record<string, string> = {
  octubre: 'Octubre',
  noviembre: 'Noviembre',
  diciembre: 'Diciembre',
}

// Colores Ferrer
const GREEN_600 = '#16a34a'  // verde primario
const BLUE_700  = '#1d4ed8'  // azul secundario Ferrer

const BENCHMARK_CTR        = 0.20  // %
const BENCHMARK_VIEWABILITY = 85   // %


// ── Helpers ────────────────────────────────────────────────────────────────

function fmt(n: number) { return n.toLocaleString('es-PE') }
function fmtK(n: number) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000)     return (n / 1_000).toFixed(0) + 'K'
  return n.toString()
}

// Normaliza nombres de región: quita prefijos y renombra Lima capital
function normalizeRegion(name: string): string {
  const clean = name
    .replace(/^Region de\s+/i, '')
    .replace(/^Departamento de\s+/i, '')
    .trim()
  if (clean.toLowerCase() === 'lima') return 'Lima'
  return clean
}

function groupBy<T extends { impressions: number; clicks: number; ctr: number }>(
  arr: T[], key: keyof T,
): (Omit<T, 'mes'> & { impressions: number; clicks: number; ctr: number })[] {
  const map = new Map<string, { impressions: number; clicks: number }>()
  arr.forEach((r) => {
    const k = String(r[key])
    const prev = map.get(k) ?? { impressions: 0, clicks: 0 }
    map.set(k, { impressions: prev.impressions + r.impressions, clicks: prev.clicks + r.clicks })
  })
  return Array.from(map.entries())
    .map(([k, v]) => ({
      [key]: k,
      impressions: v.impressions,
      clicks: v.clicks,
      ctr: v.impressions ? +(v.clicks / v.impressions * 100).toFixed(4) : 0,
    } as unknown as Omit<T, 'mes'> & { impressions: number; clicks: number; ctr: number }))
    .sort((a, b) => b.impressions - a.impressions)
}

// ── Componentes ─────────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string
  value: string
  sub?: string
  icon: React.ElementType
  benchmark?: number
  actual?: number
  accentHex?: string
  tooltip?: string
}

function KpiCard({ label, value, sub, icon: Icon, benchmark, actual, accentHex = GREEN_600, tooltip }: KpiCardProps) {
  const diff   = benchmark !== undefined && actual !== undefined ? actual - benchmark : null
  const above  = diff !== null && diff >= 0
  const color  = diff !== null ? (above ? GREEN_600 : '#d97706') : accentHex

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-card h-full"
      style={{ backgroundImage: `linear-gradient(to right, ${color}18, transparent 60%)` }}
    >
      <div className="h-full px-4 py-4 flex flex-col justify-center gap-2">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
          {tooltip && (
            <TooltipProvider delayDuration={100}>
              <UITooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/50 hover:text-muted-foreground cursor-help shrink-0" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[220px] text-xs text-center">
                  {tooltip}
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="shrink-0 text-muted-foreground">
            <Icon className="h-9 w-9" />
          </div>
          <div className="flex-1 min-w-0 text-right">
            <div className="text-3xl font-bold text-foreground leading-tight">{value}</div>
            {diff !== null && (
              <div className="flex items-center gap-1 mt-0.5 justify-end">
                {above
                  ? <TrendingUp  className="h-3 w-3 text-green-600" />
                  : <TrendingDown className="h-3 w-3 text-amber-600" />}
                <span className={`text-xs font-medium ${above ? 'text-green-600' : 'text-amber-600'}`}>
                  {above ? '+' : ''}{diff.toFixed(2)}% vs {benchmark! % 1 === 0 ? benchmark : benchmark! < 1 ? benchmark!.toFixed(2) : benchmark!.toFixed(1)}%
                </span>
              </div>
            )}
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

// Gráfica de benchmark estilo BenchmarkBarChart del multichannel
function BenchmarkBar({ tasa, benchmark, label, decimals = 1 }: {
  tasa: number; benchmark: number; label: string; icon?: React.ReactNode; decimals?: number
}) {
  const above = tasa >= benchmark
  const color = above ? GREEN_600 : '#d97706'  // amber-600 cuando está por debajo

  // La barra mayor siempre al 100%, la menor proporcional
  const altActual    = above ? 100 : (benchmark > 0 ? (tasa / benchmark) * 100 : 0)
  const altBenchmark = above ? (tasa > 0 ? (benchmark / tasa) * 100 : 0) : 100
  const diff = tasa - benchmark

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex justify-center gap-5">
        {/* Barra benchmark */}
        <div className="flex flex-col items-center w-14">
          <span className="text-xs font-medium text-muted-foreground mb-1">{benchmark.toFixed(decimals)}%</span>
          <div className="w-full rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 relative" style={{ height: 140 }}>
            <div
              className="absolute bottom-0 left-0 right-0 bg-gray-400 dark:bg-gray-500"
              style={{ height: `${altBenchmark}%`, borderRadius: altBenchmark === 100 ? 6 : '0 0 6px 6px' }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground mt-1">Benchmark</span>
        </div>
        {/* Barra campaña */}
        <div className="flex flex-col items-center w-14">
          <span className="text-xs font-bold mb-1" style={{ color }}>{tasa.toFixed(decimals)}%</span>
          <div className="w-full rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 relative" style={{ height: 140 }}>
            <div
              className="absolute bottom-0 left-0 right-0"
              style={{ height: `${altActual}%`, backgroundColor: color, borderRadius: altActual === 100 ? 6 : '0 0 6px 6px' }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground mt-1">Campaña</span>
        </div>
      </div>

      <div className="flex items-center gap-1">
        {above
          ? <TrendingUp  className="h-3.5 w-3.5" style={{ color: GREEN_600 }} />
          : <TrendingDown className="h-3.5 w-3.5" style={{ color: '#d97706' }} />}
        <span className="text-xs font-medium" style={{ color }}>
          {above ? '+' : ''}{diff.toFixed(2)}%
        </span>
        <span className="ml-1 inline-flex items-center justify-center h-5 w-5 rounded-full text-white"
          style={{ backgroundColor: color }}>
          {above
            ? <Check className="h-3 w-3" strokeWidth={3} />
            : <span className="text-[11px] font-bold leading-none">!</span>}
        </span>
      </div>

      <span className="text-sm font-medium">{label}</span>
    </div>
  )
}

function KpiSkeleton() {
  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Skeleton className="h-8 w-8 rounded-md" />
          <div className="flex-1 space-y-1">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-6 w-28" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const PAGE_SIZE_OPTIONS = [10, 25, 50]

function TablaRegion({ ciudadData, benchmarkCtr }: { ciudadData: ReturnType<typeof groupBy>; benchmarkCtr: number }) {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const totales = useMemo(() => {
    const imp = ciudadData.reduce((s, c) => s + c.impressions, 0)
    const clk = ciudadData.reduce((s, c) => s + c.clicks, 0)
    const ctr = imp ? +(clk / imp * 100).toFixed(2) : 0
    return { imp, clk, ctr }
  }, [ciudadData])
  const totalPages = Math.ceil(ciudadData.length / pageSize)
  const rows = ciudadData.slice(page * pageSize, (page + 1) * pageSize)

  const ctrTotalAbove = totales.ctr >= benchmarkCtr

  return (
    <div className="space-y-3">
    {/* Card resumen totales */}
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardContent className="py-3 px-6">
        <div className="grid grid-cols-4 divide-x divide-gray-100 dark:divide-gray-800">
          <div className="text-center px-4">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-0.5">Regiones</p>
            <p className="text-lg font-bold text-foreground">{ciudadData.length}</p>
          </div>
          <div className="text-center px-4">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-0.5 flex items-center justify-center gap-1"><Eye className="h-3 w-3" />Impresiones</p>
            <p className="text-lg font-bold text-foreground">{fmt(totales.imp)}</p>
          </div>
          <div className="text-center px-4">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-0.5 flex items-center justify-center gap-1"><MousePointerClick className="h-3 w-3" />Clicks</p>
            <p className="text-lg font-bold text-foreground">{fmt(totales.clk)}</p>
          </div>
          <div className="text-center px-4">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-0.5 flex items-center justify-center gap-1"><Activity className="h-3 w-3" />CTR</p>
            <div className="flex items-center justify-center gap-1">
              {ctrTotalAbove ? <TrendingUp className="h-4 w-4 text-green-600" /> : <TrendingDown className="h-4 w-4 text-amber-600" />}
              <p className="text-lg font-bold" style={{ color: ctrTotalAbove ? GREEN_600 : '#d97706' }}>{totales.ctr.toFixed(2)}%</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <MapIcon className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-medium">Detalle por región</CardTitle>
        </div>
        <span className="inline-flex items-baseline gap-1 rounded-md border border-gray-200 dark:border-gray-700 bg-muted/40 px-2 py-0.5 text-xs">
          <span className="font-semibold text-black dark:text-white">{ciudadData.length}</span>
          <span className="text-muted-foreground">regiones</span>
        </span>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-hidden rounded-b-xl">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800 text-sm">
            <thead className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-muted-foreground">
                  <div className="flex items-center gap-1.5"><MapIcon className="h-3.5 w-3.5" />Región</div>
                </th>
                <th className="px-4 py-3 text-right font-semibold text-muted-foreground">
                  <div className="flex items-center justify-end gap-1.5"><Eye className="h-3.5 w-3.5" />Impresiones</div>
                </th>
                <th className="px-4 py-3 text-right font-semibold text-muted-foreground">
                  <div className="flex items-center justify-end gap-1.5"><MousePointerClick className="h-3.5 w-3.5" />Clicks</div>
                </th>
                <th className="px-4 py-3 text-right font-semibold text-muted-foreground">
                  <div className="flex items-center justify-end gap-1.5"><Activity className="h-3.5 w-3.5" />CTR</div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
              {rows.map((c, i) => {
                const ctrAbove = c.ctr >= benchmarkCtr
                return (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-black dark:text-white">{(c as CiudadItem).region}</td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground">{fmt(c.impressions)}</td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground">{fmt(c.clicks)}</td>
                    <td className="px-4 py-2.5 text-right font-semibold" style={{ color: ctrAbove ? GREEN_600 : '#d97706' }}>
                      <div className="flex items-center justify-end gap-1">
                        {ctrAbove ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {c.ctr.toFixed(2)}%
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Filas por página:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }}
              className="border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-background text-xs"
            >
              {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="ml-2">{page * pageSize + 1}–{Math.min((page + 1) * pageSize, ciudadData.length)} de {ciudadData.length}</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`w-6 h-6 rounded text-xs font-medium transition-colors ${
                  i === page
                    ? 'text-white'
                    : 'text-muted-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                style={i === page ? { backgroundColor: GREEN_600 } : {}}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
    </div>
  )
}

// ── Página ──────────────────────────────────────────────────────────────────

export function FerrerProgrammatic2025Page() {
  useDocumentTitle('Ferrer Programmatic 2025')
  const { data, isLoading, error } = useDashReport<ProgrammaticData>('ferrer/programmatic/2025')
  const [mesActivo, setMesActivo] = useState<string>('todos')

  const mesesDisponibles = useMemo(() => {
    if (!data) return []
    return MESES.filter((m) => data.meses[m])
  }, [data])

  const ciudadData = useMemo(() => {
    if (!data) return []
    const src = mesActivo === 'todos'
      ? mesesDisponibles.flatMap((m) => data.meses[m].ciudad)
      : (data.meses[mesActivo]?.ciudad ?? [])
    // Normalizar nombres y excluir UNKNOWN antes de agrupar
    const normalizada = src
      .filter((c) => c.region.toUpperCase() !== 'UNKNOWN')
      .map((c) => ({ ...c, region: normalizeRegion(c.region) }))
    return groupBy(normalizada, 'region')
  }, [data, mesActivo, mesesDisponibles])

  const creativoData = useMemo(() => {
    if (!data) return []
    const src = mesActivo === 'todos'
      ? mesesDisponibles.flatMap((m) => data.meses[m].creativo)
      : (data.meses[mesActivo]?.creativo ?? [])
    return groupBy(src, 'creativo')
  }, [data, mesActivo, mesesDisponibles])

  const dispositivoData = useMemo(() => {
    if (!data) return []
    const src = mesActivo === 'todos'
      ? mesesDisponibles.flatMap((m) => data.meses[m].dispositivo)
      : (data.meses[mesActivo]?.dispositivo ?? [])
    return groupBy(src, 'dispositivo')
  }, [data, mesActivo, mesesDisponibles])

  const diarioData = useMemo(() => {
    if (!data) return []
    const src = mesActivo === 'todos'
      ? mesesDisponibles.flatMap((m) => data.meses[m].diario)
      : (data.meses[mesActivo]?.diario ?? [])
    return src.sort((a, b) => a.fecha.localeCompare(b.fecha))
  }, [data, mesActivo, mesesDisponibles])

  const perf = useMemo((): Performance | null => {
    if (!data) return null
    if (mesActivo === 'todos') {
      const periodos = mesesDisponibles.map((m) => data.meses[m].performance).filter(Boolean)
      if (!periodos.length) return null
      const imp  = periodos.reduce((s, p) => s + p.impressions, 0)
      const clk  = periodos.reduce((s, p) => s + p.clicks, 0)
      const uniq = periodos.reduce((s, p) => s + p.unique_impressions, 0)
      const view = periodos.reduce((s, p) => s + p.viewability * p.impressions, 0) / imp
      return {
        impressions: imp, clicks: clk,
        ctr: imp ? +(clk / imp * 100).toFixed(4) : 0,
        viewability: +view.toFixed(1),
        unique_impressions: uniq,
        frequency: uniq ? +(imp / uniq).toFixed(2) : 0,
      }
    }
    return data.meses[mesActivo]?.performance ?? null
  }, [data, mesActivo, mesesDisponibles])

  // ── Chart configs ────────────────────────────────────────────────────────

  const BLUE_SHADES = ['#bfdbfe','#93c5fd','#60a5fa','#3b82f6','#2563eb','#1d4ed8','#1e40af']

  const ciudadColors = useMemo(() => {
    const max = ciudadData.reduce((m, c) => Math.max(m, c.impressions), 0)
    return ciudadData.map((c) => {
      if (max <= 0 || c.impressions <= 0) return BLUE_SHADES[0]
      const ratio = Math.sqrt(c.impressions / max)
      const idx = Math.min(BLUE_SHADES.length - 1, Math.max(0, Math.round(ratio * (BLUE_SHADES.length - 1))))
      return BLUE_SHADES[idx]
    })
  }, [ciudadData])

  const diarioChartData = useMemo(() => {
    type DayEntry = { fecha: string; day: number; month: string; fechaDisplay: string; impressions: number | null; clicks: number | null }
    const entries: DayEntry[] = diarioData.map((d) => {
      const dt = new Date(d.fecha + 'T12:00:00')
      return {
        fecha: d.fecha,
        day: dt.getDate(),
        month: dt.toLocaleDateString('es-PE', { month: 'short' }),
        fechaDisplay: `${dt.getDate()}`,
        impressions: d.impressions,
        clicks: d.clicks,
      }
    })

    // Insertar entradas null en los huecos entre meses para crear espacio visual
    const result: DayEntry[] = []
    for (let i = 0; i < entries.length; i++) {
      result.push(entries[i])
      if (i < entries.length - 1) {
        const curr = new Date(entries[i].fecha + 'T12:00:00')
        const next = new Date(entries[i + 1].fecha + 'T12:00:00')
        const diffDays = Math.round((next.getTime() - curr.getTime()) / 86400000)
        if (diffDays > 1) {
          for (let g = 1; g < diffDays; g++) {
            const gap = new Date(curr.getTime() + g * 86400000)
            const gFecha = gap.toISOString().slice(0, 10)
            result.push({
              fecha: gFecha,
              day: gap.getDate(),
              month: gap.toLocaleDateString('es-PE', { month: 'short' }),
              fechaDisplay: `${gap.getDate()}`,
              impressions: null,
              clicks: null,
            })
          }
        }
      }
    }
    return result
  }, [diarioData])

  // Por cada mes: índice central (para nombre) e índices de días a mostrar
  const { monthCenterIndices, showDayIndices } = useMemo(() => {
    const groups = new Map<string, number[]>()
    diarioChartData.forEach((d, i) => {
      const ym = d.fecha.slice(0, 7)
      if (!groups.has(ym)) groups.set(ym, [])
      groups.get(ym)!.push(i)
    })

    const NOMBRES: Record<string, string> = {
      '10': 'Octubre', '11': 'Noviembre', '12': 'Diciembre',
    }

    const centers = new Map<number, string>()
    const showDays = new Set<number>()
    const singleMonth = groups.size === 1

    groups.forEach((indices, ym) => {
      // Centro del mes → nombre completo
      centers.set(indices[Math.floor(indices.length / 2)], NOMBRES[ym.slice(5, 7)] ?? ym.slice(5, 7))

      // Mes único: todos los días; múltiples meses: primero, cada 7 y último
      // En multi-mes, omitir el tick de pos%7===0 si está a menos de 3 del último (evita colisión día 29/30/31)
      indices.forEach((idx, pos) => {
        if (singleMonth) {
          showDays.add(idx)
        } else if (pos === 0 || pos === indices.length - 1) {
          showDays.add(idx)
        } else if (pos % 7 === 0 && pos < indices.length - 3) {
          showDays.add(idx)
        }
      })
    })

    return { monthCenterIndices: centers, showDayIndices: showDays }
  }, [diarioChartData])

  // Corchetes de tipo de campaña: CPM = Oct+Nov, CPC = Dic
  const campaignBrackets = useMemo(() => {
    if (mesActivo === 'todos') {
      let cpmCount = 0, cpcCount = 0
      for (const d of diarioChartData) {
        const mm = d.fecha.slice(5, 7)
        if (mm === '10' || mm === '11') cpmCount++
        else if (mm === '12') cpcCount++
      }
      if (cpcCount === 0 || cpmCount === 0) return []
      return [
        { label: 'CPM', count: cpmCount },
        { label: 'CPC', count: cpcCount },
      ]
    }
    // Vista de mes individual
    const tipo = mesActivo === 'diciembre' ? 'CPC' : 'CPM'
    return [{ label: tipo, count: 1 }]
  }, [diarioChartData, mesActivo])

  // Primera fecha completa de cada mes nuevo (para ReferenceLine)
  const monthBoundaries = useMemo(() => {
    const seen = new Set<string>()
    const result: { fecha: string }[] = []
    for (const d of diarioChartData) {
      const mm = d.fecha.slice(5, 7)
      if (!seen.has(mm)) {
        seen.add(mm)
        if (seen.size > 1) result.push({ fecha: d.fecha })
      }
    }
    return result
  }, [diarioChartData])

  const diarioChartConfig = {
    impressions: { label: 'Impresiones', color: GREEN_600 },
    clicks:      { label: 'Clicks',      color: BLUE_700 },
  } satisfies ChartConfig

  const noAxes = {
    grid: { display: false },
    border: { display: false },
    ticks: { display: false },
  }

  const barCiudadOptions = useMemo(() => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 70 } },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const, align: 'right' as const, offset: 4, clip: false,
        color: '#6b7280', font: { size: 11 },
        formatter: (v: number) => fmt(v),
      },
    },
    scales: {
      x: { ...noAxes, beginAtZero: true },
      y: { grid: { display: false }, border: { display: false }, ticks: { color: '#6b7280', font: { size: 11 }, padding: 8 }, afterFit: (axis: { width: number }) => { axis.width = 140 } },
    },
  }), [])

  const barCreativoOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 30 } },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const, align: 'top' as const, offset: 4, clip: false,
        color: '#6b7280', font: { size: 11 },
        formatter: (v: number) => fmtK(v),
      },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: '#6b7280', font: { size: 11 } } },
      y: { ...noAxes, beginAtZero: true },
    },
  }), [])


  // ── Skeleton global ──────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">
        <Skeleton className="h-20 w-full rounded-xl" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <KpiSkeleton key={i} />)}
        </div>
        <Skeleton className="h-[280px] w-full rounded-xl" />
      </div>
    )
  }

  if (error || !data || !perf) {
    return (
      <div className="p-6">
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardContent className="flex items-center justify-center h-40 text-sm text-muted-foreground">
            Error al cargar el reporte programático.
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const mapaData = ciudadData.map((c) => ({
    label: (c as CiudadItem).region,
    value: c.impressions,
  }))

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      {/* ── Header ── */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="py-4 px-6">
          <div className="flex items-center justify-between gap-4">
            {/* Izquierda: logo Tonal + títulos */}
            <div className="flex items-center gap-5">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-12 w-auto dark:invert" />
              <div className="border-l border-gray-200 dark:border-gray-700 pl-5">
                <h1 className="text-xl font-bold text-foreground leading-tight">
                  Reporte Programmatic 2025
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Campaña Display · Octubre - Noviembre - Diciembre
                </p>
              </div>
            </div>
            {/* Derecha: logo Ferrer */}
            <img
              src="/ferrer-programmatic/ferrer_logo.png"
              alt="Ferrer"
              className="h-12 w-auto flex-shrink-0"
            />
          </div>
        </CardContent>
      </Card>

      {/* ── KPIs + Benchmarks ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Izquierda: filtro + 2 filas de KPIs */}
        <div className="lg:col-span-2 flex flex-col gap-4">

          {/* Fila 1: Filtro */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-card px-4 py-3 flex items-center justify-between gap-3 w-full">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Período</span>
            <div className="flex gap-2 flex-1 justify-end">
              {(['todos', ...mesesDisponibles] as string[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMesActivo(m)}
                  className={`flex-1 px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${
                    mesActivo === m
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-transparent text-muted-foreground border-gray-300 dark:border-gray-600 hover:border-green-500 hover:text-green-600'
                  }`}
                >
                  {m === 'todos' ? 'Anual' : MES_LABELS[m]}
                </button>
              ))}
            </div>
          </div>

          {/* Filas 2 y 3: KPIs */}
          <div className="grid grid-cols-3 gap-4 flex-1 content-stretch">
            <KpiCard label="Impresiones"  value={fmtK(perf.impressions)}        sub={fmt(perf.impressions)}        icon={Eye}              accentHex={GREEN_600} tooltip="Número total de veces que el anuncio fue mostrado en pantalla." />
            <KpiCard label="Unique Reach" value={fmtK(perf.unique_impressions)}  sub={fmt(perf.unique_impressions)} icon={Users}            accentHex={GREEN_600} tooltip="Número de usuarios únicos alcanzados por la campaña." />
            <KpiCard label="Clicks"       value={fmt(perf.clicks)}                                                  icon={MousePointerClick} accentHex={GREEN_600} tooltip="Total de interacciones del usuario con el anuncio." />
            <KpiCard label="CTR"          value={perf.ctr.toFixed(2) + '%'}      actual={perf.ctr}                  benchmark={BENCHMARK_CTR}         icon={Activity}  accentHex={BLUE_700} tooltip="Click-Through Rate: porcentaje de impresiones que generaron un clic." />
            <KpiCard label="Viewability"  value={perf.viewability + '%'}         actual={perf.viewability}          benchmark={BENCHMARK_VIEWABILITY}  icon={BarChart2} accentHex={BLUE_700} tooltip="Porcentaje de impresiones visibles según estándar IAB (≥50% del anuncio visible por ≥1 segundo)." />
            <KpiCard label="Frequency"    value={String(perf.frequency)}                                            icon={Repeat2}          accentHex={GREEN_600} tooltip="Promedio de veces que un mismo usuario único vio el anuncio." />
          </div>
        </div>

        {/* Derecha: Benchmark */}
        <Card className="border border-gray-300 dark:border-gray-700">
          <CardHeader className="space-y-0 pb-2">
            <div className="w-full text-center text-sm font-medium">Benchmark</div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-row justify-around gap-4">
              <BenchmarkBar
                tasa={perf.ctr}
                benchmark={BENCHMARK_CTR}
                label="CTR"
                decimals={2}
                icon={<Activity className="h-4 w-4 text-muted-foreground" />}
              />
              <BenchmarkBar
                tasa={perf.viewability}
                benchmark={BENCHMARK_VIEWABILITY}
                label="Viewability"
                icon={<Eye className="h-4 w-4 text-muted-foreground" />}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Rendimiento diario ── */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Rendimiento diario</CardTitle>
          </div>
          {/* Leyenda arriba derecha */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-5 rounded-full" style={{ background: GREEN_600 }} />
              <span>Impresiones</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-5 rounded-full" style={{ background: BLUE_700 }} />
              <span>Clicks</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 px-1">
          {/* Corchetes de tipo de campaña sobre la gráfica */}
          {campaignBrackets.length > 0 && (
            <div className="flex pt-2 gap-2" style={{ paddingLeft: 49, paddingRight: 59 }}>
              {campaignBrackets.map((b) => (
                <div key={b.label} style={{ flex: b.count }} className="flex flex-col items-center">
                  <span className="text-[11px] font-semibold leading-none mb-1" style={{ color: b.label === 'CPC' ? '#166534' : BLUE_700 }}>Estrategia {b.label}</span>
                  <div
                    className="w-full"
                    style={{
                      height: 8,
                      borderTop: `2px solid ${b.label === 'CPC' ? '#166534' : BLUE_700}`,
                      borderLeft: `2px solid ${b.label === 'CPC' ? '#166534' : BLUE_700}`,
                      borderRight: `2px solid ${b.label === 'CPC' ? '#166534' : BLUE_700}`,
                      borderRadius: '3px 3px 0 0',
                      opacity: 0.7,
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          <ChartContainer config={diarioChartConfig} className="!aspect-auto w-full" style={{ height: 280 }}>
            <ComposedChart data={diarioChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-impressions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={GREEN_600} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={GREEN_600} stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
              <XAxis
                dataKey="fecha"
                tickLine={false}
                axisLine={false}
                tick={(props) => {
                  const { x, y, index } = props
                  const item = diarioChartData[index]
                  if (!item) return <g />
                  const monthLabel = monthCenterIndices.get(index)
                  const showDay = showDayIndices.has(index)
                  return (
                    <g transform={`translate(${x},${y})`}>
                      {showDay && (
                        <text x={0} y={0} dy={14} textAnchor="middle" fontSize={10} fill="#374151">{item.day}</text>
                      )}
                      {monthLabel && (
                        <text x={0} y={0} dy={32} textAnchor="middle" fontSize={11} fontWeight={600} fill="#6b7280">{monthLabel}</text>
                      )}
                    </g>
                  )
                }}
                interval={0}
                height={50}
              />
              <YAxis yAxisId="left"  tickLine={false} axisLine={false} tickFormatter={fmtK} tick={{ fontSize: 11 }} width={45} />
              <YAxis yAxisId="right" tickLine={false} axisLine={false} tickFormatter={fmtK} tick={{ fontSize: 11 }} width={35} orientation="right" />
              <ChartTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null
                  const fecha = payload[0]?.payload?.fecha ?? ''
                  return (
                    <div className="rounded-md border border-border bg-background px-3 py-2 text-xs shadow-md space-y-1.5">
                      <p className="font-medium text-foreground">{fecha}</p>
                      {payload.map((entry) => (
                        <div key={String(entry.dataKey)} className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                          <span className="text-muted-foreground">{entry.dataKey === 'impressions' ? 'Impresiones' : 'Clicks'}</span>
                          <span className="font-medium ml-auto pl-4">{Number(entry.value).toLocaleString('es-PE')}</span>
                        </div>
                      ))}
                    </div>
                  )
                }}
              />
              {/* Líneas separadoras de mes */}
              {monthBoundaries.map((b) => (
                <ReferenceLine
                  key={b.fecha}
                  x={b.fecha}
                  yAxisId="left"
                  stroke="#94a3b8"
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                />
              ))}
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="impressions"
                stroke={GREEN_600}
                strokeWidth={2}
                fill="url(#grad-impressions)"
                dot={false}
                activeDot={{ r: 4, fill: GREEN_600, stroke: 'white', strokeWidth: 2 }}
                connectNulls={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="clicks"
                stroke={BLUE_700}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: BLUE_700, stroke: 'white', strokeWidth: 2 }}
                connectNulls={false}
              />
            </ComposedChart>
          </ChartContainer>

        </CardContent>
      </Card>

      {/* ── Descripción de estrategias ── */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className={`py-4 px-6 grid divide-x divide-gray-100 dark:divide-gray-800 ${mesActivo === 'todos' ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {mesActivo !== 'diciembre' && (
            <div className="text-center px-6">
              <span className="text-sm font-semibold" style={{ color: BLUE_700 }}>Estrategia CPM</span>
              <p className="text-xs text-muted-foreground leading-snug mt-1">
                Audiencias de personas con intenciones o intereses específicos relacionados al tema.
              </p>
            </div>
          )}
          {(mesActivo === 'todos' || mesActivo === 'diciembre') && (
            <div className="text-center px-6">
              <span className="text-sm font-semibold" style={{ color: '#166534' }}>Estrategia CPC</span>
              <p className="text-xs text-muted-foreground leading-snug mt-1">
                Uso de contextos; la IA usa palabras claves y busca sitios web relacionados para impactar a la audiencia que los visita.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Mapa + Barras región ── */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 items-stretch">
        {/* Barras: 70% */}
        <div className="lg:col-span-7">
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="flex items-center gap-2">
                <BarChart2 className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-medium">Impresiones por región</CardTitle>
              </div>
              <span className="inline-flex items-baseline gap-1 rounded-md border border-gray-200 dark:border-gray-700 bg-muted/40 px-2 py-0.5 text-xs">
                <span className="font-semibold text-black dark:text-white">{ciudadData.length}</span>
                <span className="text-muted-foreground">regiones</span>
              </span>
            </CardHeader>
            <CardContent className="p-0 pb-4">
              <div style={{ maxHeight: 360, overflowY: 'auto' }} className="px-4">
                <div style={{ height: Math.max(360, ciudadData.length * 26), position: 'relative' }}>
                  <Bar
                    data={{
                      labels: ciudadData.map((c) => (c as CiudadItem).region),
                      datasets: [{
                        label: 'Impresiones',
                        data: ciudadData.map((c) => c.impressions),
                        backgroundColor: ciudadColors,
                        borderRadius: 4,
                      }],
                    }}
                    options={barCiudadOptions}
                    plugins={[ChartDataLabels]}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mapa: 30% */}
        <div className="lg:col-span-3">
          <MapaDepartamentosPeru
            title="Mapa de Perú"
            data={mapaData}
            icon={MapIcon}
            formatValue={fmt}
          />
        </div>
      </div>

      {/* ── Creativos + Dispositivos ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
        <div className="lg:col-span-3 flex flex-col">
          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
              <BarChart2 className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Impresiones por formato de creativo</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ height: 260 }}>
                <Bar
                  data={{
                    labels: creativoData.map((c) => (c as CreativoItem).creativo),
                    datasets: [{
                      label: 'Impresiones',
                      data: creativoData.map((c) => c.impressions),
                      backgroundColor: GREEN_600,
                      borderRadius: 4,
                    }],
                  }}
                  options={barCreativoOptions}
                  plugins={[ChartDataLabels]}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2 flex flex-col">
        <Card className="border border-gray-300 dark:border-gray-700 flex-1 flex flex-col">
          <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-2">
            <Monitor className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">Dispositivos</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col justify-between pt-3">
            <div className="flex flex-col justify-between h-full gap-3">
              {(() => {
                const total = dispositivoData.reduce((s, d) => s + d.impressions, 0)
                return dispositivoData.map((d) => {
                  const pct = total ? (d.impressions / total) * 100 : 0
                  return (
                    <div key={(d as DispositivoItem).dispositivo}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[13px] font-medium text-foreground">{(d as DispositivoItem).dispositivo}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] text-muted-foreground">{fmt(d.impressions)}</span>
                          <span className="text-[13px] font-bold w-12 text-right" style={{ color: BLUE_700 }}>{pct.toFixed(1)}%</span>
                        </div>
                      </div>
                      <div className="h-2 w-full rounded-full bg-blue-100 dark:bg-blue-950">
                        <div
                          className="h-2 rounded-full transition-all"
                          style={{ width: `${pct}%`, minWidth: pct > 0 ? 6 : 0, background: `linear-gradient(90deg, ${BLUE_700}, #1e40af)` }}
                        />
                      </div>
                    </div>
                  )
                })
              })()}
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* ── Tabla región ── */}
      <TablaRegion ciudadData={ciudadData} benchmarkCtr={BENCHMARK_CTR} />

      {/* ── Footer ── */}
      <p className="text-center text-xs text-muted-foreground pb-4">
        Tonal Strategies · Reporte Programmatic Ferrer 2025
      </p>

    </div>
  )
}
